import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type {
  WithdrawalListApiResponse,
  WithdrawalTableFilterParams,
} from "interface/withdrawal";

const getFormattedParams = (params: WithdrawalTableFilterParams = {}) => {
  const formatted: Record<string, string | number> = {
    page: params.page ?? 1,
    limit: params.pageSize ?? 10,
  };
  if (params.sortField && params.sortOrder) {
    formatted.sort = params.sortField;
    formatted.direction = params.sortOrder === "ascend" ? "asc" : "desc";
  }
  const userIds = Array.isArray(params.user_id)
    ? params.user_id.filter(Boolean).join("-")
    : params.user_id;
  if (userIds) formatted.user_id = userIds;
  const statuses = Array.isArray(params.status)
    ? params.status.filter(Boolean).join("-")
    : params.status;
  if (statuses) formatted.status = statuses;
  const createdAt = Array.isArray(params.createdAt)
    ? params.createdAt[0]
    : params.createdAt;
  if (createdAt) formatted.createdAt = createdAt;
  return formatted;
};

export const withdrawalApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWithdrawals: builder.query<
      WithdrawalListApiResponse,
      WithdrawalTableFilterParams
    >({
      query: (params) => ({
        url: API.GET_WITHDRAWAL,
        method: "GET",
        params: getFormattedParams(params),
      }),
      providesTags: ["Withdrawal"],
    }),
    approveWithdrawal: builder.mutation<
      { statusCode: number; message: string; data?: unknown },
      string
    >({
      query: (id) => ({
        url: `/${API.GET_WITHDRAWAL}/${id}/approve`,
        method: "PUT",
      }),
      invalidatesTags: ["Withdrawal"],
    }),
    rejectWithdrawal: builder.mutation<
      { statusCode: number; message: string; data?: unknown },
      { id: string; reason: string }
    >({
      query: ({ id, reason }) => ({
        url: `/${API.GET_WITHDRAWAL}/${id}/reject`,
        method: "PUT",
        body: { reason },
      }),
      invalidatesTags: ["Withdrawal"],
    }),
    downloadWithdrawalReport: builder.mutation<
      { data?: { fileUrl?: string } },
      WithdrawalTableFilterParams | void
    >({
      query: (params) => ({
        url: `/${API.GET_WITHDRAWAL}/download`,
        method: "GET",
        params: getFormattedParams(params ?? {}),
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          const fileUrl = (result.data?.data as Record<string, unknown>)?.fileUrl;
          if (typeof fileUrl === "string") {
            window.open(fileUrl, "_blank");
          }
        } catch (error) {
          console.error("Failed to download withdrawal report:", error);
        }
      },
    }),
  }),
});

export const {
  useGetWithdrawalsQuery,
  useApproveWithdrawalMutation,
  useRejectWithdrawalMutation,
  useDownloadWithdrawalReportMutation,
} = withdrawalApi;
